import { useState } from "react";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";
import { profileInitials } from "../lib/utils";

const STATES = ['VIC','NSW','QLD','WA','SA','TAS','ACT','NT'];
const MEDIUM_OPTIONS = ['Painting','Drawing','Photography','Printmaking','Sculpture','Ceramics','Textiles','Digital','Video','Installation','Performance','Mixed Media'];

export function EditProfileModal({ profile, onClose, onSaved }) {
  const isArtist = profile?.user_type === 'artist';
  const [form, setForm] = useState({
    display_name:  String(profile?.display_name  || ''),
    full_name:     String(profile?.full_name     || ''),
    bio:           String(profile?.bio           || ''),
    location:      String(profile?.location      || ''),
    state:         String(profile?.state         || ''),
    website:       String(profile?.website       || ''),
    instagram:     String(profile?.instagram     || ''),
    abn:           String(profile?.abn           || ''),
    first_nations: Boolean(profile?.first_nations),
    mediums:       Array.isArray(profile?.mediums) ? profile.mediums : [],
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(profile?.avatar_url || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Guard after all hooks — React rules require hooks to always run
  if (!profile) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const toggleMedium = (m) => {
    setForm(f => ({
      ...f,
      mediums: f.mediums.includes(m) ? f.mediums.filter(x => x !== m) : [...f.mediums, m],
    }));
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      let avatar_url = profile?.avatar_url || null;
      if (avatarFile) {
        const ext = avatarFile.name.split('.').pop().toLowerCase();
        const path = `avatars/${profile?.id}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('portfolio-images')
          .upload(path, avatarFile, { upsert: true });
        if (upErr) throw upErr;
        const { data: { publicUrl } } = supabase.storage.from('portfolio-images').getPublicUrl(path);
        avatar_url = publicUrl;
      }
      const updates = {
        display_name: form.display_name.trim() || null,
        full_name:    form.full_name.trim()    || null,
        bio:          form.bio.trim()          || null,
        location:     form.location.trim()     || null,
        state:        form.state               || null,
        website:      form.website.trim()      || null,
        instagram:    form.instagram.replace(/^@/,'').trim() || null,
        abn:          form.abn.trim()          || null,
        avatar_url,
        updated_at: new Date().toISOString(),
        ...(isArtist && { first_nations: form.first_nations, mediums: form.mediums }),
      };
      const { error: dbErr } = await supabase.from('profiles').update(updates).eq('id', profile.id);
      if (dbErr) throw dbErr;
      onSaved({ ...profile, ...updates });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const inp = { background:C.parch, border:`1px solid ${C.sand}`, borderRadius:6, padding:"9px 11px", fontSize:".83rem", color:C.bark, width:"100%", boxSizing:"border-box", outline:"none", fontFamily:"inherit" };
  const lbl = { fontSize:".62rem", fontFamily:"'DM Mono',monospace", letterSpacing:"1px", textTransform:"uppercase", color:C.muted, display:"block", marginBottom:4 };
  const row = { marginBottom:14 };

  const initials = profileInitials(profile);

  return (
    <div style={{ position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",background:"rgba(40,30,20,.6)",overflowY:"auto",padding:"24px 16px" }}>
      <div style={{ background:C.parch,borderRadius:12,width:"100%",maxWidth:540,padding:"28px 24px",boxShadow:"0 8px 40px rgba(0,0,0,.2)" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24 }}>
          <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.4rem",fontWeight:400,color:C.bark,margin:0 }}>Edit Profile</h2>
          <button onClick={onClose} style={{ background:"none",border:"none",fontSize:"1.2rem",cursor:"pointer",color:C.muted,padding:4 }}>✕</button>
        </div>

        {/* Avatar */}
        <div style={{ display:"flex",alignItems:"center",gap:16,marginBottom:22 }}>
          <div style={{ position:"relative" }}>
            {avatarPreview
              ? <img src={avatarPreview} alt="Avatar" style={{ width:72,height:72,borderRadius:"50%",objectFit:"cover",border:`3px solid ${C.clay}` }}/>
              : <div style={{ width:72,height:72,borderRadius:"50%",background:`linear-gradient(135deg,${C.clay},${C.barkL})`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Mono',monospace",fontSize:"1.1rem",color:"#fff",border:`3px solid ${C.clay}` }}>{initials}</div>
            }
          </div>
          <label style={{ cursor:"pointer" }}>
            <div className="btn-dk" style={{ fontSize:".68rem",padding:"8px 14px",display:"inline-block" }}>Change photo</div>
            <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display:"none" }} onChange={handleAvatarPick}/>
          </label>
        </div>

        <div style={{ display:"flex",gap:12,marginBottom:14 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>Display name</label>
            <input style={inp} value={form.display_name} onChange={e=>set('display_name',e.target.value)} placeholder="How you appear on the platform"/>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>Full name</label>
            <input style={inp} value={form.full_name} onChange={e=>set('full_name',e.target.value)} placeholder="Legal name"/>
          </div>
        </div>

        <div style={row}>
          <label style={lbl}>Bio</label>
          <textarea style={{ ...inp,minHeight:90,resize:"vertical" }} value={form.bio} onChange={e=>set('bio',e.target.value)} placeholder={isArtist ? "Describe your practice, background and approach…" : "Describe your gallery, its history and vision…"}/>
        </div>

        <div style={{ display:"flex",gap:12,marginBottom:14 }}>
          <div style={{ flex:2 }}>
            <label style={lbl}>Location</label>
            <input style={inp} value={form.location} onChange={e=>set('location',e.target.value)} placeholder="e.g. Naarm / Melbourne"/>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>State</label>
            <select style={inp} value={form.state} onChange={e=>set('state',e.target.value)}>
              <option value="">—</option>
              {STATES.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display:"flex",gap:12,marginBottom:14 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>Website</label>
            <input style={inp} value={form.website} onChange={e=>set('website',e.target.value)} placeholder="https://…"/>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>Instagram</label>
            <input style={inp} value={form.instagram} onChange={e=>set('instagram',e.target.value)} placeholder="@handle"/>
          </div>
        </div>

        <div style={row}>
          <label style={lbl}>ABN</label>
          <input style={inp} value={form.abn} onChange={e=>set('abn',e.target.value)} placeholder="12 345 678 901"/>
        </div>

        {isArtist && (
          <>
            <div style={{ marginBottom:14 }}>
              <label style={{ ...lbl,marginBottom:8 }}>Mediums</label>
              <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                {MEDIUM_OPTIONS.map(m=>(
                  <button key={m} onClick={()=>toggleMedium(m)} style={{ padding:"5px 12px",borderRadius:20,border:`1px solid ${form.mediums.includes(m)?C.clay:C.sand}`,background:form.mediums.includes(m)?C.clay:"transparent",color:form.mediums.includes(m)?"#fff":C.bark,fontSize:".72rem",cursor:"pointer",fontFamily:"inherit" }}>{m}</button>
                ))}
              </div>
            </div>
            <label style={{ display:"flex",alignItems:"center",gap:10,marginBottom:18,cursor:"pointer",fontSize:".83rem",color:C.bark }}>
              <input type="checkbox" checked={form.first_nations} onChange={e=>set('first_nations',e.target.checked)}/>
              First Nations artist
            </label>
          </>
        )}

        {error && <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:14,fontSize:".8rem",color:C.rose }}>⚠ {error}</div>}

        <div style={{ display:"flex",gap:10 }}>
          <button type="button" className="btn-dk" style={{ flex:1,padding:"12px" }} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-p" style={{ flex:2,padding:"12px" }} onClick={handleSave} disabled={saving}>{saving?"Saving…":"Save profile"}</button>
        </div>
      </div>
    </div>
  );
}
